import { CAPABILITY_COPY } from './constants';

/**
 * Turns a role's permission codes into the handful of capabilities a License
 * Manager actually weighs when assigning it (MKT-527).
 *
 * This is a curated catalog, not a transformation. Codes are grouped by what
 * they let someone *do*, several codes collapse into one line, and the plumbing
 * every role carries is described by nothing at all. The trade is that a new
 * permission area needs copy written for it here — `countUndescribed` and the
 * accompanying test exist so that omission surfaces rather than hides.
 */

export interface RoleCapability {
  /** Stable key, for React keys and `data-testid`s. */
  key: string;
  label: string;
}

/**
 * Resources we deliberately do not describe. Each is either implied by a
 * capability we already state or is machinery a customer never reasons about
 * when picking a role:
 *
 * - `map` / `region` — the map viewport and default region behind territories.
 * - `activity_categories` — the vocabulary for logging activity, not access.
 * - `reimbursement` — whether one widget renders on a provider profile.
 * - `internal_banner` — the MedScout-internal-use banner on two tables.
 * - `company_roles` — reading the roles list, which is what makes *this screen*
 *   work; describing it back to the viewer is circular.
 */
const IGNORED_RESOURCES: readonly string[] = [
  'map',
  'region',
  'activity_categories',
  'reimbursement',
  'internal_banner',
  'company_roles',
];

/** Resources some capability below consults. Kept in step by the tests. */
const DESCRIBED_RESOURCES: readonly string[] = [
  'territory',
  'filter_sets',
  'encounter_sets',
  'advanced_searches',
  'ai',
  'territory_optimization',
  'users',
  'company_users',
  'roles',
  'settings',
  'integration_config',
];

/** Resources whose codes the "Save filters, searches…" line covers. */
const SAVED_WORK_RESOURCES = [
  'filter_sets',
  'encounter_sets',
  'advanced_searches',
];

/** Resources whose codes the user-management lines cover. */
const USER_RESOURCES = ['users', 'company_users'];

const EDIT_ACTIONS = ['write', 'delete'];
const ANY_ACTION = ['read', 'write', 'delete'];

/** A permission code split into its three segments. */
type ParsedCode = { action: string; resource: string; scope: string };

const parseCodes = (permissions: string[]): ParsedCode[] =>
  permissions.reduce<ParsedCode[]>((parsed, permission) => {
    const [action, resource, scope] = (permission ?? '').split('.');
    if (action && resource && scope) parsed.push({ action, resource, scope });

    return parsed;
  }, []);

/**
 * Capabilities a role grants, in the order they should be read: what the user
 * does day to day first, then what they administer.
 */
export const getRoleCapabilities = (
  permissions: string[] = []
): RoleCapability[] => {
  const codes = parseCodes(permissions);

  const matches = (
    actions: readonly string[],
    resources: readonly string[],
    scopes?: readonly string[]
  ) =>
    codes.some(
      (code) =>
        actions.includes(code.action) &&
        resources.includes(code.resource) &&
        (!scopes || scopes.includes(code.scope))
    );

  const capabilities: RoleCapability[] = [];
  const add = (key: string, label: string) => capabilities.push({ key, label });

  // Territories, widest scope wins — a role that can reach the whole company
  // can obviously reach its own, and saying both would be noise. `bounded` is
  // a *restriction* on someone's own territories, so it reads as "their own".
  if (matches(ANY_ACTION, ['territory'], ['company'])) {
    add('territory', CAPABILITY_COPY.territoryCompany);
  } else if (matches(ANY_ACTION, ['territory'], ['direct_report'])) {
    add('territory', CAPABILITY_COPY.territoryDirectReports);
  } else if (matches(ANY_ACTION, ['territory'])) {
    add('territory', CAPABILITY_COPY.territoryOwn);
  }

  // Filters, searches and encounter sets are three resources but one habit, so
  // they collapse into one line. Read-only access to them is worth saying;
  // nearly every role can *see* the company's saved work.
  if (matches(EDIT_ACTIONS, SAVED_WORK_RESOURCES, ['company'])) {
    add('saved-work', CAPABILITY_COPY.savedWorkCompany);
  } else if (matches(EDIT_ACTIONS, SAVED_WORK_RESOURCES)) {
    add('saved-work', CAPABILITY_COPY.savedWork);
  } else if (matches(['read'], SAVED_WORK_RESOURCES)) {
    add('saved-work', CAPABILITY_COPY.savedWorkReadOnly);
  }

  // Scout AI is on or off — `read.ai.company` is the only code in the area, and
  // there is no read-vs-write distinction to draw. Matched on any action so a
  // later `write.ai.*` reads as access rather than falling to the undescribed
  // tail.
  if (matches(ANY_ACTION, ['ai'])) {
    add('scout-ai', CAPABILITY_COPY.scoutAi);
  }

  // Territory Optimization is the same shape as Scout AI: one code
  // (`read.territory_optimization.company`) turns the whole feature on, matched
  // on any action so a later write/delete code reads as access rather than
  // falling to the undescribed tail (MKT-545). The resource parses distinctly
  // from `territory`, so the territory lines above are unaffected.
  if (matches(ANY_ACTION, ['territory_optimization'])) {
    add('territory-optimization', CAPABILITY_COPY.territoryOptimization);
  }

  // Every `export.*` code folds into one line, with two exceptions:
  // `providers_in_territory` is a different order of magnitude and MedScout's to
  // grant (see `isStaffOnlyRole`), and `company_users` is downloading the user
  // list, which anyone managing users can obviously do — calling it "table data"
  // alongside provider exports would misdescribe both.
  const exportedResources = codes
    .filter((code) => code.action === 'export')
    .map((code) => code.resource);

  if (
    exportedResources.some(
      (resource) =>
        !['providers_in_territory', ...USER_RESOURCES].includes(resource)
    )
  ) {
    add('exports', CAPABILITY_COPY.exports);
  }
  if (exportedResources.includes('providers_in_territory')) {
    add('bulk-export', CAPABILITY_COPY.bulkExport);
  }

  if (matches(EDIT_ACTIONS, USER_RESOURCES)) {
    add('users', CAPABILITY_COPY.users);
  } else if (matches(['read'], USER_RESOURCES)) {
    add('users', CAPABILITY_COPY.usersReadOnly);
  }

  // Only editing roles is worth stating — reading them is what lets someone
  // assign a role, which the checkbox beside this summary already says.
  if (matches(EDIT_ACTIONS, ['roles'])) {
    add('roles', CAPABILITY_COPY.roles);
  }

  if (matches(EDIT_ACTIONS, ['settings'])) {
    add('settings', CAPABILITY_COPY.settings);
  }

  if (matches(EDIT_ACTIONS, ['integration_config'])) {
    add('integrations', CAPABILITY_COPY.integrations);
  }

  return capabilities;
};

/**
 * How many permissions the catalog above says nothing about.
 *
 * The point is to fail loudly rather than quietly: if the API grows a resource
 * nobody has written copy for, the summary admits there is more access than it
 * describes instead of presenting an incomplete list as complete.
 */
export const countUndescribed = (permissions: string[] = []): number =>
  parseCodes(permissions).filter(
    (code) =>
      // Exports are all covered by an export capability whatever the resource.
      code.action !== 'export' &&
      !DESCRIBED_RESOURCES.includes(code.resource) &&
      !IGNORED_RESOURCES.includes(code.resource)
  ).length;

/** Exported for the test that keeps the catalog honest about its coverage. */
export const CLASSIFIED_RESOURCES: readonly string[] = [
  ...DESCRIBED_RESOURCES,
  ...IGNORED_RESOURCES,
];
